import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
  KeyRound,
  Globe,
  Sun,
  Moon,
  Eye,
  EyeOff,
  ChevronRight,
  Loader2,
  FileText,
  ShieldCheck,
  Scroll,
  RotateCcw,
  GraduationCap,
  CheckSquare,
  Trash2,
  AlertTriangle,
  Monitor,
  Smartphone,
  LogOut,
  MapPin,
  Clock,
} from 'lucide-react';
import {
  useChangePasswordMutation,
  useRequestAccountDeletionMutation,
  useCancelAccountDeletionMutation,
  useGetMeQuery,
  useGetMySessionsQuery,
  useRevokeSessionMutation,
} from '../../api/apiSlice';

// Лёгкий парсер userAgent — без библиотек, по основным сигнатурам. Хватает,
// чтобы юзер опознал свои устройства («Chrome на Windows», «Safari на iPhone»).
function parseUA(ua) {
  if (!ua) return { browser: '—', os: '—', isMobile: false };
  let browser = '—';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/OPR\//i.test(ua)) browser = 'Opera';
  else if (/YaBrowser/i.test(ua)) browser = 'Yandex';
  else if (/Chrome\//i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua)) browser = 'Safari';

  let os = '—';
  let isMobile = false;
  // iPhone/iPad UA содержит "Mac OS X" (iOS — родственник macOS), поэтому
  // проверяем мобильные ПЕРЕД десктопными — иначе iPhone попадает в macOS.
  if (/iPhone/i.test(ua)) { os = 'iOS'; isMobile = true; }
  else if (/iPad/i.test(ua)) { os = 'iPadOS'; isMobile = true; }
  else if (/Android/i.test(ua)) { os = 'Android'; isMobile = true; }
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  return { browser, os, isMobile };
}

// SettingsPage
// ────────────
// Единая страница настроек для student / instructor / admin.
//
// Секции:
//   • Аккаунт — read-only email, имя, роли
//   • Смена пароля (скрывается для Google-only аккаунтов)
//   • Предпочтения — язык, тема (через существующие механизмы i18n + theme)
//   • Правовые документы — ссылки на 7 страниц политик
//
// Полностью i18n'нута (RU/KK), переключается через useTranslation.

// Включая «Политику возврата»: возвраты НЕ производятся, но политика явно
// описана в документе. Прозрачнее показать «возвратов нет, вот почему», чем
// прятать и оставлять Покупателя в неведении.
// tone — пара классов для иконки документа. Раскрашиваем каждый отдельно,
// чтобы 7 ссылок не выглядели «семь одинаковых индиго-кружков».
const LEGAL_LINKS = [
  { to: '/legal/privacy',           icon: ShieldCheck,   key: 'privacy',          tone: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' },
  { to: '/legal/data-consent',      icon: CheckSquare,   key: 'dataConsent',      tone: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
  { to: '/legal/terms',             icon: Scroll,        key: 'terms',            tone: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300' },
  { to: '/legal/offer',             icon: FileText,      key: 'offer',            tone: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' },
  { to: '/legal/refund',            icon: RotateCcw,     key: 'refund',           tone: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300' },
  { to: '/legal/instructor-rules',  icon: GraduationCap, key: 'instructorRules',  tone: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' },
  { to: '/legal/content-policy',    icon: FileText,      key: 'contentPolicy',    tone: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' },
];

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((s) => s.auth);
  // /me получаем чтобы знать deletionScheduledAt — может оказаться, что юзер
  // запросил удаление раньше, и нам нужен баннер с возможностью отмены.
  const { data: meFresh } = useGetMeQuery();
  const effectiveUser = meFresh || user;

  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [requestDeletion, { isLoading: isRequestingDeletion }] = useRequestAccountDeletionMutation();
  const [cancelDeletion, { isLoading: isCancellingDeletion }] = useCancelAccountDeletionMutation();

  // Активные сессии — показываем для ВСЕХ ролей (даже у инструктора/админа без
  // лимита может быть полезно увидеть свои устройства и отозвать чужое).
  const { data: sessionsData, isFetching: isFetchingSessions } = useGetMySessionsQuery();
  const [revokeSession, { isLoading: isRevoking }] = useRevokeSessionMutation();
  const sessions = sessionsData?.items || [];
  const [revokingId, setRevokingId] = useState(null);

  const handleRevokeSession = async (id) => {
    setRevokingId(id);
    try {
      await revokeSession(id).unwrap();
      toast.success(t('settings.sessionRevoked'));
    } catch (err) {
      toast.error(err?.data?.message || t('errors.generic'));
    } finally {
      setRevokingId(null);
    }
  };

  const [confirmText, setConfirmText] = useState('');
  const deletionScheduledAt = effectiveUser?.deletionScheduledAt;

  const [form, setForm] = useState({
    current: '',
    next: '',
    confirm: '',
  });
  const [show, setShow] = useState({ current: false, next: false });

  // Google-only аккаунты (вошли через OAuth и пароль так и не задали) —
  // прячем секцию смены пароля: им просто нечего «менять».
  //
  // КРИТИЧНО: берём из effectiveUser (свежий /me с бэка), а НЕ из user
  // (этот снапшот из localStorage сделан при логине и не знает про новые
  // поля hasPassword/googleId — фикс был именно из-за этого, форма
  // показывалась Google-юзерам потому что флаги тянулись из stale-данных).
  const isGoogleOnly = effectiveUser?.googleId && !effectiveUser?.hasPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current || !form.next) return;
    if (form.next !== form.confirm) {
      toast.error(t('settings.passwordMismatch'));
      return;
    }
    if (form.current === form.next) {
      toast.error(t('settings.passwordSamAsCurrent'));
      return;
    }
    try {
      await changePassword({
        currentPassword: form.current,
        newPassword: form.next,
      }).unwrap();
      toast.success(t('settings.passwordChanged'));
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err?.data?.message || t('errors.generic'));
    }
  };

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem('i18nextLng', lng); } catch { /* ignore */ }
  };

  // Удаление аккаунта — двухступенчатое:
  //   1) юзер вводит «DELETE» в поле подтверждения
  //   2) дёргает мутацию → ставится 30-дневный grace
  // До окончания grace можно отменить → cancelDeletion()
  const handleRequestDeletion = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE') return;
    try {
      await requestDeletion().unwrap();
      toast.success(t('settings.deleteAccountSuccess'));
      setConfirmText('');
    } catch (err) {
      toast.error(err?.data?.message || t('errors.generic'));
    }
  };
  const handleCancelDeletion = async () => {
    try {
      await cancelDeletion().unwrap();
      toast.success(t('settings.deleteAccountCancelled'));
    } catch (err) {
      toast.error(err?.data?.message || t('errors.generic'));
    }
  };

  const formatScheduledDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(i18n.resolvedLanguage === 'kk' ? 'kk-KZ' : 'ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6 pb-16">
        {/* Простой компактный заголовок страницы */}
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            {t('settings.title')}
          </h1>
        </div>

      {/* Password */}
      {!isGoogleOnly && (
        <Section icon={KeyRound} title={t('settings.passwordSection')} color="blue">
          <form onSubmit={handleSubmit} className="space-y-3">
            <PasswordField
              label={t('settings.currentPassword')}
              value={form.current}
              onChange={(v) => setForm((p) => ({ ...p, current: v }))}
              visible={show.current}
              onToggle={() => setShow((p) => ({ ...p, current: !p.current }))}
            />
            <PasswordField
              label={t('settings.newPassword')}
              value={form.next}
              onChange={(v) => setForm((p) => ({ ...p, next: v }))}
              visible={show.next}
              onToggle={() => setShow((p) => ({ ...p, next: !p.next }))}
              hint={t('settings.newPasswordHint')}
            />
            <PasswordField
              label={t('settings.confirmNewPassword')}
              value={form.confirm}
              onChange={(v) => setForm((p) => ({ ...p, confirm: v }))}
              visible={show.next}
              onToggle={() => setShow((p) => ({ ...p, next: !p.next }))}
            />
            <button
              type="submit"
              disabled={isLoading || !form.current || !form.next || !form.confirm}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 text-white px-5 py-2.5 text-sm font-semibold shadow-md shadow-sky-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {t('settings.changeButton')}
            </button>
          </form>
        </Section>
      )}

      {isGoogleOnly && (
        <Section icon={KeyRound} title={t('settings.passwordSection')} color="blue">
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-200 dark:ring-amber-800/40 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
              {t('settings.googleAccountWarning')}
            </p>
          </div>
        </Section>
      )}

      {/* Preferences */}
      <Section icon={Globe} title={t('settings.preferencesSection')} color="cyan">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Language */}
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-gray-800 bg-slate-50/50 dark:bg-gray-800/30 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
              <Globe className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
              {t('settings.language')}
            </div>
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-white dark:bg-gray-900 ring-1 ring-slate-200 dark:ring-gray-700 p-1">
              {[
                { id: 'ru', label: 'Русский', short: 'RU' },
                { id: 'kk', label: 'Қазақша', short: 'KK' },
              ].map((l) => {
                const isActive = i18n.resolvedLanguage === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => changeLang(l.id)}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'bg-gradient-to-br from-cyan-500 to-blue-500 text-white shadow-md shadow-cyan-500/30'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Theme */}
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-gray-800 bg-slate-50/50 dark:bg-gray-800/30 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
              <Sun className="w-4 h-4 text-amber-500 dark:hidden" />
              <Moon className="w-4 h-4 text-indigo-300 hidden dark:inline" />
              {t('settings.theme')}
            </div>
            <ThemeToggle />
          </div>
        </div>
      </Section>

      {/* Active sessions / устройства */}
      <Section
        icon={Monitor}
        title={t('settings.sessionsSection')}
        subtitle={t('settings.sessionsSubtitle')}
        color="emerald"
        rightSlot={
          sessions.length > 0 ? (
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-2.5 py-0.5 text-xs font-bold tabular-nums shadow-sm">
              {sessions.length}
            </span>
          ) : null
        }
      >
        {isFetchingSessions && sessions.length === 0 ? (
          <div className="flex items-center justify-center py-6 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.sessionsEmpty')}</p>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => {
              const { browser, os, isMobile } = parseUA(s.userAgent);
              const Icon = isMobile ? Smartphone : Monitor;
              const lastUsed = new Date(s.lastUsedAt).toLocaleString(
                i18n.resolvedLanguage === 'kk' ? 'kk-KZ' : 'ru-RU',
                { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }
              );
              return (
                <li
                  key={s.id}
                  className={`flex items-start gap-3 rounded-xl border p-3 ${
                    s.isCurrent
                      ? 'border-indigo-300 bg-indigo-50/50 dark:border-indigo-700 dark:bg-indigo-900/20'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                  }`}
                >
                  <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    s.isCurrent
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-800/50 dark:text-indigo-200'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="font-semibold text-sm text-gray-900 dark:text-white">
                        {browser} · {os}
                      </span>
                      {s.isCurrent && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wide">
                          {t('settings.sessionCurrent')}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {s.ipAddress && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {s.ipAddress}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {lastUsed}
                      </span>
                    </div>
                  </div>

                  {!s.isCurrent && (
                    <button
                      type="button"
                      onClick={() => handleRevokeSession(s.id)}
                      disabled={isRevoking && revokingId === s.id}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 px-3 py-1.5 text-xs font-semibold disabled:opacity-50 transition"
                      title={t('settings.sessionRevokeCta')}
                    >
                      {isRevoking && revokingId === s.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <LogOut className="w-3.5 h-3.5" />
                      )}
                      <span className="hidden sm:inline">{t('settings.sessionRevokeCta')}</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {/* Banner — если уже запросили удаление, висит сверху */}
      {deletionScheduledAt && (
        <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-900 dark:text-red-100">
              {t('settings.deleteAccountScheduled')}
            </p>
            <p className="text-xs text-red-800 dark:text-red-200 mt-0.5">
              {t('settings.deleteAccountScheduledOn', { date: formatScheduledDate(deletionScheduledAt) })}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancelDeletion}
            disabled={isCancellingDeletion}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >
            {isCancellingDeletion ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RotateCcw className="w-3.5 h-3.5" />
            )}
            {t('settings.deleteAccountCancelCta')}
          </button>
        </div>
      )}

      {/* Legal documents */}
      <Section icon={FileText} title={t('settings.legalSection')} subtitle={t('settings.legalSubtitle')} color="slate">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {LEGAL_LINKS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="group flex items-center gap-3 rounded-xl ring-1 ring-slate-200 dark:ring-gray-800 bg-white dark:bg-gray-900 hover:ring-slate-300 dark:hover:ring-gray-700 hover:-translate-y-0.5 hover:shadow-md p-3.5 transition-all"
              >
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${item.tone}`}>
                  <Icon className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {t(`legal.${item.key}`)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {t(`legal.${item.key}Short`)}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 group-hover:translate-x-0.5 transition shrink-0" />
              </Link>
            );
          })}
        </div>
      </Section>

      {/* ───── DANGER ZONE ─────
          Удаление аккаунта. UI намеренно «уродливый» (красный, требует
          ввода DELETE) — это защита от случайного клика. Не убираем для
          юзеров с уже запланированным удалением (только баннер сверху). */}
      {!deletionScheduledAt && (
        <section className="rounded-2xl border-2 border-red-200 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/10 overflow-hidden">
          <div className="px-5 py-3 border-b border-red-100 dark:border-red-900/40 flex items-center gap-2 bg-red-100/60 dark:bg-red-950/30">
            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-red-900 dark:text-red-100">
                {t('settings.dangerSection')}
              </h2>
              <p className="text-xs text-red-700 dark:text-red-300/80 mt-0.5">
                {t('settings.dangerSubtitle')}
              </p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <p className="text-sm text-red-900 dark:text-red-100 font-medium">
              {t('settings.deleteAccountTitle')}
            </p>
            <p className="text-xs text-red-800 dark:text-red-200/90 leading-relaxed">
              {t('settings.deleteAccountDesc')}
            </p>
            <p className="text-[11px] text-red-700/80 dark:text-red-300/70 italic leading-relaxed">
              {t('settings.deleteAccountWhatKept')}
            </p>
            <div className="pt-2">
              <label className="text-xs font-medium text-red-900 dark:text-red-100 block mb-1">
                {t('settings.deleteAccountConfirmText')}
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="flex-1 rounded-xl border border-red-300 dark:border-red-900/50 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white font-mono tracking-wider uppercase focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <button
                  type="button"
                  onClick={handleRequestDeletion}
                  disabled={
                    isRequestingDeletion ||
                    confirmText.trim().toUpperCase() !== 'DELETE'
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 text-white px-5 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isRequestingDeletion ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {t('settings.deleteAccountConfirmCta')}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
      </div>
    </div>
  );
};

// ─── pieces ─────────────────────────────────────────────

// Цветная палитра для иконок секций. Tailwind JIT не поддерживает динамические
// классы — хардкодим градиенты для каждого ключа. Каждая секция получает
// собственный тон, чтобы UI читался как живая, а не «всё индиго».
const SECTION_PALETTE = {
  violet:  'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-md shadow-violet-500/20',
  blue:    'bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-md shadow-sky-500/20',
  emerald: 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20',
  amber:   'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/20',
  rose:    'bg-gradient-to-br from-rose-500 to-pink-500 text-white shadow-md shadow-rose-500/20',
  slate:   'bg-gradient-to-br from-slate-600 to-gray-700 text-white shadow-md shadow-slate-500/20',
  cyan:    'bg-gradient-to-br from-cyan-500 to-blue-500 text-white shadow-md shadow-cyan-500/20',
};

const Section = ({ icon: Icon, title, subtitle, color = 'violet', rightSlot, children }) => (
  <section className="rounded-2xl bg-white dark:bg-gray-900 ring-1 ring-slate-200/60 dark:ring-gray-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-gray-800">
      <div className="flex items-center gap-3 min-w-0">
        <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${SECTION_PALETTE[color] || SECTION_PALETTE.violet}`}>
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">{title}</h2>
          {subtitle && (
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {rightSlot}
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

const PasswordField = ({ label, value, onChange, visible, onToggle, hint }) => (
  <div>
    <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-1">
      {label}
    </label>
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 pr-10 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        tabIndex={-1}
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
    {hint && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
  </div>
);

const ThemeToggle = () => {
  // Простой theme toggle. Глобальный darkMode уже управляется (видим по классам
  // dark:* во всех компонентах) — обычно через `class` на <html>. Дёргаем за это.
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark')
  );
  const apply = (next) => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', next);
    try { localStorage.setItem('theme', next ? 'dark' : 'light'); } catch { /* ignore */ }
    setIsDark(next);
  };
  return (
    <div className="grid grid-cols-2 gap-1 rounded-xl bg-white dark:bg-gray-900 ring-1 ring-slate-200 dark:ring-gray-700 p-1">
      <button
        type="button"
        onClick={() => apply(false)}
        className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
          !isDark
            ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md shadow-amber-500/30'
            : 'text-gray-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800'
        }`}
      >
        <Sun className="w-3.5 h-3.5" /> Light
      </button>
      <button
        type="button"
        onClick={() => apply(true)}
        className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
          isDark
            ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/30'
            : 'text-gray-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800'
        }`}
      >
        <Moon className="w-3.5 h-3.5" /> Dark
      </button>
    </div>
  );
};

export default SettingsPage;
